/**
 * A town that has just been taken.
 *
 * *"you need to rebuild the other town after battle"*, and the owner meant it
 * literally: a captured settlement should arrive **wrecked**, not renamed. That
 * is a statement about the simulation and not about the paint — rubble on the
 * ground, roofs that are not there, a wall with holes in it and a population
 * that has been through something.
 *
 * Nothing here is a new mechanism. Every roof comes down through the same
 * `removeBuilding(w, i, true)` a besieger's ram brings it down with, so it
 * leaves the same rubble, remembering the same building type, clearable and
 * rebuildable by the same two commands the player already has — `Cmd.ClearRubble`
 * and `Cmd.RebuildRubble`, 17 and 18, which have existed since the first siege.
 * A sacked town is therefore not a special kind of town; it is an ordinary town
 * that something happened to.
 *
 * ## What is deliberately spared
 *
 * The **Hearth** never comes down. `w.hearth` is the one building whose loss
 * ends the run (`gameOver`), and a captured town that killed you on arrival
 * would be a cruel joke rather than a rebuilding job. It is damaged instead —
 * badly, so the first thing the player does in their new holding is mend it.
 *
 * The **walls** are knocked about rather than levelled. A breach is what a
 * storming leaves; a bare ring of nothing is what a demolition leaves, and the
 * two read completely differently on the ground.
 *
 * ## Determinism
 *
 * Every roll comes off the world's own `rng` and the order of the rolls is
 * fixed by the order of the loops, so a wreck applied through the command log
 * is identical on every machine and in every replay. This is why it is a
 * command (`Cmd.WreckTown`) and not something the code that opens a town does
 * to the world behind the simulation's back.
 */

import { UF, WallKind } from '../types.ts';
import { buildingType } from '../../data/buildings.ts';
import { removeBuilding, recomputeManaCap, recomputePopCap, recomputeStorage, type World } from '../world.ts';
import { killUnit } from './combat.ts';
import { recount } from './towers.ts';
import { nextFloat, type Rng } from '../../core/rng.ts';
import { LogTone, pushLog } from '../events.ts';

/** How hard the Hearth itself is hit, as a fraction of its health, at wreck 1. */
const HEARTH_TOLL = 0.6;
/** A wall tile's chance of being knocked out entirely scales by this. */
const WALL_BREACH_SHARE = 0.55;
/** …and the rest of the circuit is left standing at this much health. */
const WALL_BATTERED_HP = 0.45;

export interface SackReport {
  /** Roofs brought down into rubble. */
  ruined: number;
  /** Wall and tower tiles knocked out of the circuit entirely. */
  breached: number;
  /** Souls who are not there any more. */
  lost: number;
}

/**
 * Bring `amount` (0..1) of this settlement down.
 *
 * Returns what it actually cost, so a harness can assert on it and the log can
 * say something true. `amount <= 0` is a no-op and returns zeroes — which is
 * the ordinary case, because most towns are entered without having been
 * stormed first.
 */
export function wreckTown(w: World, rng: Rng, amount: number): SackReport {
  const f = Math.max(0, Math.min(1, amount));
  const report: SackReport = { ruined: 0, breached: 0, lost: 0 };
  if (f <= 0) return report;

  /**
   * The roofs, walked in slot order.
   *
   * Slot order rather than "the ones nearest the gate" on purpose: which
   * quarter of a town burns is a fact about a fight that was resolved on the
   * strategic map and that nobody watched, so inventing a geography for it
   * would be inventing a battle. A flat roll over the whole town is the honest
   * shape of "you were not there".
   */
  const doomed: number[] = [];
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (i === w.hearth) continue;
    if (nextFloat(rng) < f) doomed.push(i);
  }
  // Collected first and removed second: `removeBuilding` writes the building
  // layer and unassigns workers, and mutating the store inside its own walk is
  // the class of bug this project has already paid for twice.
  for (const i of doomed) {
    removeBuilding(w, i, true);
    report.ruined++;
  }

  // The Hearth takes it too, and survives it. A town whose Hearth is at a
  // third is a town with a job to do on the first morning.
  if (w.hearth >= 0 && b.isAlive(w.hearth)) {
    const max = b.maxHp[w.hearth];
    b.hp[w.hearth] = Math.max(max * 0.2, b.hp[w.hearth] - max * HEARTH_TOLL * f);
  }

  /**
   * The circuit: a share of it gone, the rest of it battered.
   *
   * Written straight into the layers rather than through `damageWall`, because
   * `damageWall` is the combat pass — it raises breach events, mourns, stains
   * and looks for who did it, none of which is true of a wall that was knocked
   * down last month in a fight nobody here saw. `wallVersion` is bumped once at
   * the end, which is what the renderer and the curtain watch.
   */
  const map = w.map;
  for (let k = 0; k < map.wall.length; k++) {
    const kind = map.wall[k];
    if (kind === WallKind.None) continue;
    if (nextFloat(rng) < f * WALL_BREACH_SHARE) {
      map.wall[k] = WallKind.None;
      map.wallHp[k] = 0;
      map.wallMaxHp[k] = 0;
      map.wallTier[k] = 0;
      map.stain[k] = Math.min(255, map.stain[k] + 60);
      report.breached++;
    } else {
      map.wallHp[k] = Math.max(1, map.wallHp[k] * (1 - f * (1 - WALL_BATTERED_HP)));
    }
  }
  if (report.breached > 0) {
    map.wallVersion++;
    w.dirty.flow = true;
  }

  /**
   * And the people.
   *
   * Through `killUnit` rather than by emptying the store, so the age bands, the
   * grave debt, the job assignments and the population count all move the way
   * they move when somebody dies — which is the whole reason `killUnit` exists.
   * Unmourned: nine hundred floating skulls over a town the player has not seen
   * yet is not a bereavement, it is a loading screen.
   *
   * The town is never emptied outright. Somebody has to be standing there to
   * clear the first pile of rubble, or the holding is a picture rather than a
   * place — the same base-case argument the Drowned Court's opening thralls
   * are.
   */
  const u = w.units;
  const alive: number[] = [];
  for (let i = 0; i < u.slots.high; i++) if (u.slots.alive[i] !== 0) alive.push(i);
  const keep = Math.min(alive.length, 6);
  for (const i of alive) {
    if (alive.length - report.lost <= keep) break;
    if (nextFloat(rng) >= f) continue;
    killUnit(w, i, -1, false);
    report.lost++;
  }

  /**
   * …and the head count, now, rather than on the next tick.
   *
   * `stepPopulation` recomputes `pop` from live models every tick, so leaving
   * it stale would have corrected itself inside a frame — and been *wrong for
   * that frame*, which is enough. `pop` is hashed and saved: a world saved
   * between this command and the next tick would carry a population that does
   * not match its own units, and would load as a different world from the one
   * that was written. Counted rather than subtracted, because `killUnit` is
   * entitled to have taken somebody this loop did not.
   */
  let heads = 0;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] !== 0 && (u.flags[i] & UF.Villager) !== 0) heads++;
  }
  w.pop = heads;
  w.counts.villagers = heads;

  recomputePopCap(w);
  recomputeStorage(w);
  recomputeManaCap(w);
  recount(w);
  w.dirty.jobs = true;
  w.dirty.districts = true;

  pushLog(
    w.events,
    report.ruined > 0 || report.breached > 0
      ? `What is left of this place: ${report.ruined} roofs down, ${report.breached} tiles out of the wall, ${report.lost} gone.`
      : 'The town stands, more or less.',
    LogTone.Bad,
  );
  return report;
}

/** What a wreck of this depth will cost, for a tooltip. Draws no random numbers. */
export function wreckPreview(w: World, amount: number): { roofs: number; souls: number } {
  const f = Math.max(0, Math.min(1, amount));
  let roofs = 0;
  for (let i = 0; i < w.buildings.slots.high; i++) {
    if (w.buildings.slots.alive[i] === 0 || i === w.hearth) continue;
    if (buildingType(w.buildings.typeId[i])) roofs++;
  }
  return { roofs: Math.round(roofs * f), souls: Math.round(w.pop * f) };
}
